import type { JSONContent } from "@tiptap/react";

const PHONE_IDS = new Set(["provider_phone", "clinic_phone"]);
const EMAIL_IDS = new Set(["provider_email", "clinic_email"]);

/**
 * Resolves a mention variable into the appropriate Tiptap JSON content.
 *
 * Special cases:
 *  - clinic_logo → emailImage node
 *  - phone fields → text with tel: link
 *  - email fields → text with mailto: link
 *  - URL content → label as linked text
 *  - default → plain text node
 *
 * Returns null when content is missing (mention should stay unresolved).
 */
export function resolveMention(
  id: string,
  label: string,
  content: string | undefined,
): JSONContent | null {
  if (!content) return null;

  if (id === "clinic_logo") {
    return {
      type: "emailImage",
      attrs: { src: content, alt: "Clinic Logo", width: "auto" },
    };
  }

  if (PHONE_IDS.has(id)) {
    const digits = content.replace(/\D/g, "");
    return {
      type: "text",
      text: content,
      marks: [{ type: "link", attrs: { href: `tel:${digits}` } }],
    };
  }

  if (EMAIL_IDS.has(id)) {
    return {
      type: "text",
      text: content,
      marks: [{ type: "link", attrs: { href: `mailto:${content}` } }],
    };
  }

  if (/^https?:\/\//.test(content)) {
    return {
      type: "text",
      text: label,
      marks: [{ type: "link", attrs: { href: content } }],
    };
  }

  return { type: "text", text: content };
}

/** True for unmarked text nodes that are purely punctuation separators (e.g. " | ", ", "). */
function isSeparator(node: JSONContent): boolean {
  return (
    node.type === "text" &&
    !!node.text &&
    !node.marks?.length &&
    /^\s*[|,\-–—·•]\s*$/.test(node.text)
  );
}

/** Strips leading/trailing separator text nodes left dangling after mention removal. */
function trimSeparators(nodes: JSONContent[]): JSONContent[] {
  let start = 0;
  let end = nodes.length;
  while (start < end && isSeparator(nodes[start])) start++;
  while (end > start && isSeparator(nodes[end - 1])) end--;
  return nodes.slice(start, end);
}

/**
 * Recursively walks a Tiptap JSON tree and resolves all mention nodes
 * using the supplied variable values map.
 *
 * Unresolvable mentions (no matching value) are removed entirely.
 * Dangling separator text nodes (e.g. " | ") are trimmed, and
 * parent nodes whose content becomes empty are also removed.
 */
export function hydrateContent(
  nodes: JSONContent[],
  values: Record<string, string>,
): JSONContent[] {
  return nodes.flatMap((node) => {
    if (node.type === "mention") {
      const id: string = node.attrs?.id ?? "";
      const label: string = node.attrs?.label ?? id;
      return resolveMention(id, label, values[id]) ?? [];
    }

    if (node.content) {
      const hydrated = hydrateContent(node.content, values);
      const cleaned = trimSeparators(hydrated);
      if (cleaned.length === 0) return [];
      return { ...node, content: cleaned };
    }

    return node;
  });
}
